import { logEvent, Analytics } from 'firebase/analytics';
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAnalyticsInstance } from './firebase';
import { isPWAInstalled } from './device';

/**
 * Firebase Analytics 服务类
 */
class FirebaseAnalyticsService {
  private analytics: Analytics | null = null;
  private analyticsApp: FirebaseApp | null = null;
  private isInitialized = false;

  /**
   * 初始化Firebase Analytics项目（使用本地配置）
   */
  initializeAnalyticsApp(): FirebaseApp | null {
    console.log('初始化Firebase Analytics项目');
    if (this.analyticsApp) {
      console.log('Firebase Analytics 应用已经初始化，返回现有实例');
      return this.analyticsApp;
    }

    try {
      // 获取本地配置
      const analyticsConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
        databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || ''
      };
      // 检查是否已经存在同名的应用
      const existingApps = getApps();

      if (existingApps.length > 0) {
        this.analyticsApp = existingApps[0];
        console.log('使用现有的 Firebase Analytics 应用实例');
      } else {
        // 创建新的Analytics专用Firebase应用
        this.analyticsApp = initializeApp(analyticsConfig);
        console.log('创建新的 Firebase Analytics 应用实例');
      }
      // 初始化Analytics
      this.analytics = getAnalyticsInstance();
      this.isInitialized = this.analytics !== null;

      if (this.isInitialized) {
        console.log('Firebase Analytics 初始化成功');
      } else {
        console.error('Firebase Analytics 初始化失败');
      }

      return this.analyticsApp;
    } catch (error) {
      console.error('Firebase Analytics 应用初始化失败:', error);
      return null;
    }
  }

  /**
   * 获取 Analytics 实例
   */
  private getAnalyticsInstance(): Analytics | null {
    if (!this.isInitialized) {
      this.analytics = getAnalyticsInstance();
      this.isInitialized = this.analytics !== null;
    }
    return this.analytics;
  }

  /**
   * 记录自定义事件
   * @param eventName 事件名称
   * @param eventParameters 事件参数（可选）
   */
  trackEvent(eventName: string, eventParameters?: Record<string, any>): void {
    try {
      const analytics = this.getAnalyticsInstance();
      if (analytics) {
        logEvent(analytics, eventName, eventParameters);
        console.log(`Analytics 事件已记录: ${eventName}`, eventParameters);
      } else {
        console.warn('Firebase Analytics 未初始化，无法记录事件');
      }
    } catch (error) {
      console.error('记录 Analytics 事件时出错:', error);
    }
  }

  /**
   * 记录页面浏览事件
   * @param pageName 页面名称
   * @param pageTitle 页面标题（可选）
   */
  trackPageView(pageName: string, pageTitle?: string): void {
    this.trackEvent('page_view', {
      page_name: pageName,
      page_title: pageTitle || pageName,
      timestamp: Date.now()
    });
  }

  /**
   * 记录用户行为事件
   * @param action 用户行为（如：click, submit, view等）
   * @param category 行为类别（如：button, form, page等）
   * @param label 行为标签（可选）
   * @param value 行为值（可选）
   */
  trackUserAction(
    action: string,
    category: string,
    label?: string,
    value?: number
  ): void {
    this.trackEvent('user_action', {
      action,
      category,
      label,
      value,
      timestamp: Date.now()
    });
  }

  /**
   * 记录游戏相关事件
   * @param gameName 游戏名称
   * @param action 游戏行为（如：start, end, win, lose等）
   * @param betAmount 投注金额（可选）
   * @param winAmount 赢得金额（可选）
   */
  trackGameEvent(
    gameName: string,
    action: string,
    betAmount?: number,
    winAmount?: number
  ): void {
    this.trackEvent('game_event', {
      game_name: gameName,
      action,
      bet_amount: betAmount,
      win_amount: winAmount,
      timestamp: Date.now()
    });
  }

  /**
   * 记录登录事件
   * @param loginMethod 登录方式
   * @param userId 用户ID（可选）
   */
  trackLogin(loginMethod: string, userId?: string): void {
    this.trackEvent('user_login', {
      login_method: loginMethod,
      user_id: userId,
      timestamp: Date.now()
    });
  }

  /**
   * 记录注册事件
   * @param registerMethod 注册方式
   * @param userId 用户ID（可选）
   */
  trackRegister(registerMethod: string, userId?: string): void {
    this.trackEvent('user_register', {
      register_method: registerMethod,
      user_id: userId,
      timestamp: Date.now()
    });
  }

  /**
   * 记录充值事件
   * @param amount 充值金额
   * @param paymentMethod 支付方式
   * @param currency 货币类型
   */
  trackDeposit(amount: number, paymentMethod: string, currency: string = 'BRL'): void {
    this.trackEvent('deposit', {
      amount,
      payment_method: paymentMethod,
      currency,
      timestamp: Date.now()
    });
  }

  /**
   * 记录提现事件
   * @param amount 提现金额
   * @param withdrawalMethod 提现方式
   * @param status 状态
   */
  trackWithdrawal(amount: number, withdrawalMethod: string, status: string): void {
    this.trackEvent('withdrawal', {
      amount,
      withdrawal_method: withdrawalMethod,
      status,
      timestamp: Date.now()
    });
  }

  /**
   * 记录分享事件
   * @param platform 分享平台
   * @param contentType 内容类型
   * @param contentId 内容ID
   */
  trackShare(platform: string, contentType: string, contentId: string): void {
    this.trackEvent('share', {
      platform,
      content_type: contentType,
      content_id: contentId,
      timestamp: Date.now()
    });
  }

  /**
   * 记录下载事件
   * @param downloadType 下载类型
   * @param fileName 文件名
   */
  trackDownload(downloadType: string, fileName: string): void {
    this.trackEvent('download', {
      download_type: downloadType,
      file_name: fileName,
      timestamp: Date.now()
    });
  }

  /**
   * 记录首次打开事件
   * @param additionalParams 额外参数（可选）
   */
  trackFirstOpen(additionalParams?: Record<string, any>): void {
    this.trackEvent('first_open', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
 * 记录PWA安装提示出现事件
 * @param additionalParams 额外参数（可选）
 */
  trackPwaInstallPromptShown(additionalParams?: Record<string, any>): void {
    this.trackEvent('pwa_install_prompt_shown', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录PWA点击确认安装按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackPwaInstallPromptConfirmed(additionalParams?: Record<string, any>): void {
    this.trackEvent('pwa_install_prompt_confirmed', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录PWA点击取消安装按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackPwaInstallPromptDismissed(additionalParams?: Record<string, any>): void {
    this.trackEvent('pwa_install_prompt_dismissed', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录PWA安装完成事件
   * @param additionalParams 额外参数（可选）
   */
  trackPwaInstalled(additionalParams?: Record<string, any>): void {
    this.trackEvent('pwa_installed', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录首次手动PWA打开事件
   * @param additionalParams 额外参数（可选）
   */
  trackPwaFirstOpen(additionalParams?: Record<string, any>): void {
    this.trackEvent('pwa_firstopen', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录APK点击确认下载按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackApkDownloadPromptConfirmed(additionalParams?: Record<string, any>): void {
    this.trackEvent('apk_download_prompt_confirmed', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录APK点击取消下载按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackApkDownloadPromptDismissed(additionalParams?: Record<string, any>): void {
    this.trackEvent('apk_download_prompt_dismissed', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击注册按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickRegisterButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_register_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击登录按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickLoginButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_login_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击搜索按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickSearchButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_search_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击菜单栏按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickMenuButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_menu_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击大Banner事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickBannerMain(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_banner_main', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击小Banner1事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickBannerSmall1(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_banner_small_1', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击小Banner2事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickBannerSmall2(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_banner_small_2', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击小Banner3事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickBannerSmall3(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_banner_small_3', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击跑马灯事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickMarquee(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_marquee', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击分类标签事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickCategoryTag(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_category_tag', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击游戏入口图标事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickGameEntryIcon(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_game_entry_icon', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击导航栏home事件
   * @param additionalParams 额外参数（可选）
   */
  trackClickNavHome(additionalParams?: Record<string, any>): void {
    this.trackEvent('click_nav_home', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击导航栏Promotion事件
   * @param additionalParams 额外参数（可选）
   */
  trackClickNavPromotion(additionalParams?: Record<string, any>): void {
    this.trackEvent('click_nav_promotion', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击导航栏Deposit事件
   * @param additionalParams 额外参数（可选）
   */
  trackClickNavDeposit(additionalParams?: Record<string, any>): void {
    this.trackEvent('click_nav_deposit', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击导航栏Withdraw事件
   * @param additionalParams 额外参数（可选）
   */
  trackClickNavWithdraw(additionalParams?: Record<string, any>): void {
    this.trackEvent('click_nav_withdraw', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击导航栏Profile事件
   * @param additionalParams 额外参数（可选）
   */
  trackClickNavProfile(additionalParams?: Record<string, any>): void {
    this.trackEvent('click_nav_profile', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击浮窗事件
   * @param additionalParams 额外参数（可选）
   */
  trackHomeClickFloatingWindow(additionalParams?: Record<string, any>): void {
    this.trackEvent('homeclick_floating_window', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签events事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavEvents(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_events', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签rebate事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavRebate(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_rebate', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签vip事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavVip(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_vip', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签Redemption Code事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavRedemptionCode(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_redemptioncode', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签History事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavHistory(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_history', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion导航标签Pending事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickNavPending(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_nav_pending', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表宝箱邀请事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickTreasureInvite(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_treasure_invite', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表转盘事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickWheel(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_wheel', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表vip事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickVip(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_vip', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表推广员事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickAffiliate(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_affiliate', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表返水事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickRebate(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_rebate', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表红包雨事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickRedEnvelope(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_redenvelope', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表日救援金事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickDailyRescue(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_dailyrescue', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Promotion列表周救援金事件
   * @param additionalParams 额外参数（可选）
   */
  trackPromotionClickWeeklyRescue(additionalParams?: Record<string, any>): void {
    this.trackEvent('promotionclick_weeklyrescue', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击充值页现在支付按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackPayClickNowButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('payclick_now_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击提现页提现信息方式按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackWithdrawClickMethodButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('withdrawclick_method_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击提现页现在提现按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackWithdrawClickNowButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('withdrawclick_now_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击profile页PDeposit按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickDepositButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_deposit_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击profile页Withdraw按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickWithdrawButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_withdraw_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页vip按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickVipButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_vip_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页reports按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickReportsButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_reports_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页invite按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickInviteButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_invite_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页redemptionCode按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickRedemptionCodeButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_redemption_code_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页support按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickSupportButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_support_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页securityCenter按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickSecurityCenterButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_security_center_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页logout按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickLogoutButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_logout_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击Profile页站内信按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackProfileClickInboxButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('profileclick_inbox_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页Notification按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickNotificationButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_notification_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页Announcement按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickAnnouncementButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_Announcement_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页在线客服按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickOnlineSupportButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_onlinesupport_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页facebook按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickFacebookButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_facebook_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页telegram按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickTelegramButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_telegram_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 记录点击message center页whatsapp按钮事件
   * @param additionalParams 额外参数（可选）
   */
  trackMessageCenterClickWhatsappButton(additionalParams?: Record<string, any>): void {
    this.trackEvent('messagecenterclick_whatsapp_button', {
      timestamp: Date.now(),
      ...additionalParams
    });
  }

  /**
   * 检查并记录首次手动PWA打开事件（如果确实是首次打开）
   * @param additionalParams 额外参数（可选）
   * @returns 是否是首次打开
   */
  checkAndTrackPwaFirstOpen(additionalParams?: Record<string, any>): boolean {
    // 首先检查是否在PWA环境中
    if (!isPWAInstalled()) {
      console.log('Firebase Analytics: 不在PWA环境中，跳过首次PWA打开事件记录');
      return false;
    }

    const storageKey = 'firebase_pwa_first_open_tracked';

    // 检查是否已经记录过首次PWA打开
    if (typeof window !== 'undefined' && window.localStorage) {
      const hasTracked = window.localStorage.getItem(storageKey);

      if (!hasTracked) {
        // 记录首次PWA打开事件
        this.trackPwaFirstOpen(additionalParams);

        // 标记为已记录
        window.localStorage.setItem(storageKey, 'true');

        console.log('Firebase Analytics: 首次PWA打开事件已记录');
        return true;
      } else {
        console.log('Firebase Analytics: 首次PWA打开事件已记录过，跳过');
        return false;
      }
    }

    // 如果localStorage不可用，仍然记录事件
    this.trackPwaFirstOpen(additionalParams);
    return true;
  }

  /**
   * 检查并记录首次打开事件（如果确实是首次打开）
   * @param additionalParams 额外参数（可选）
   * @returns 是否是首次打开
   */
  checkAndTrackFirstOpen(additionalParams?: Record<string, any>): boolean {
    const storageKey = 'firebase_first_open_tracked';

    // 检查是否已经记录过首次打开
    if (typeof window !== 'undefined' && window.localStorage) {
      const hasTracked = window.localStorage.getItem(storageKey);

      if (!hasTracked) {
        // 记录首次打开事件
        this.trackFirstOpen(additionalParams);

        // 标记为已记录
        window.localStorage.setItem(storageKey, 'true');

        console.log('Firebase Analytics: 首次打开事件已记录');
        return true;
      } else {
        console.log('Firebase Analytics: 首次打开事件已记录过，跳过');
        return false;
      }
    }

    // 如果localStorage不可用，仍然记录事件
    this.trackFirstOpen(additionalParams);
    return true;
  }

  /**
   * 记录错误事件
   * @param errorType 错误类型
   * @param errorMessage 错误信息
   * @param errorCode 错误代码（可选）
   */
  trackError(errorType: string, errorMessage: string, errorCode?: string): void {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      error_code: errorCode,
      timestamp: Date.now()
    });
  }

  /**
   * 检查 Analytics 是否已初始化
   */
  isAnalyticsInitialized(): boolean {
    return this.isInitialized && this.analytics !== null;
  }

  /**
   * 重置 Analytics 实例（用于测试或重新初始化）
   */
  reset(): void {
    this.analytics = null;
    this.isInitialized = false;
    console.log('Firebase Analytics 实例已重置');
  }
}

// 创建单例实例
const firebaseAnalyticsService = new FirebaseAnalyticsService();

// 导出便捷函数
export const trackEvent = (eventName: string, eventParameters?: Record<string, any>) => {
  firebaseAnalyticsService.trackEvent(eventName, eventParameters);
};

export const trackPageView = (pageName: string, pageTitle?: string) => {
  firebaseAnalyticsService.trackPageView(pageName, pageTitle);
};

export const trackUserAction = (action: string, category: string, label?: string, value?: number) => {
  firebaseAnalyticsService.trackUserAction(action, category, label, value);
};

export const trackGameEvent = (gameName: string, action: string, betAmount?: number, winAmount?: number) => {
  firebaseAnalyticsService.trackGameEvent(gameName, action, betAmount, winAmount);
};

export const trackLogin = (loginMethod: string, userId?: string) => {
  firebaseAnalyticsService.trackLogin(loginMethod, userId);
};

export const trackRegister = (registerMethod: string, userId?: string) => {
  firebaseAnalyticsService.trackRegister(registerMethod, userId);
};

export const trackDeposit = (amount: number, paymentMethod: string, currency?: string) => {
  firebaseAnalyticsService.trackDeposit(amount, paymentMethod, currency);
};

export const trackWithdrawal = (amount: number, withdrawalMethod: string, status: string) => {
  firebaseAnalyticsService.trackWithdrawal(amount, withdrawalMethod, status);
};

export const trackShare = (platform: string, contentType: string, contentId: string) => {
  firebaseAnalyticsService.trackShare(platform, contentType, contentId);
};

export const trackDownload = (downloadType: string, fileName: string) => {
  firebaseAnalyticsService.trackDownload(downloadType, fileName);
};

export const trackFirstOpen = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackFirstOpen(additionalParams);
};

export const checkAndTrackFirstOpen = (additionalParams?: Record<string, any>) => {
  return firebaseAnalyticsService.checkAndTrackFirstOpen(additionalParams);
};

export const trackPwaInstallPromptShown = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPwaInstallPromptShown(additionalParams);
};

export const trackPwaInstallPromptConfirmed = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPwaInstallPromptConfirmed(additionalParams);
};

export const trackPwaInstallPromptDismissed = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPwaInstallPromptDismissed(additionalParams);
};

export const trackPwaInstalled = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPwaInstalled(additionalParams);
};

export const trackPwaFirstOpen = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPwaFirstOpen(additionalParams);
};

export const checkAndTrackPwaFirstOpen = (additionalParams?: Record<string, any>) => {
  return firebaseAnalyticsService.checkAndTrackPwaFirstOpen(additionalParams);
};

export const trackApkDownloadPromptConfirmed = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackApkDownloadPromptConfirmed(additionalParams);
};

export const trackApkDownloadPromptDismissed = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackApkDownloadPromptDismissed(additionalParams);
};

export const trackHomeClickRegisterButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickRegisterButton(additionalParams);
};

export const trackHomeClickLoginButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickLoginButton(additionalParams);
};

export const trackHomeClickSearchButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickSearchButton(additionalParams);
};

export const trackHomeClickMenuButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickMenuButton(additionalParams);
};

export const trackHomeClickBannerMain = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickBannerMain(additionalParams);
};

export const trackHomeClickBannerSmall1 = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickBannerSmall1(additionalParams);
};

export const trackHomeClickBannerSmall2 = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickBannerSmall2(additionalParams);
};

export const trackHomeClickBannerSmall3 = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickBannerSmall3(additionalParams);
};

export const trackHomeClickMarquee = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickMarquee(additionalParams);
};

export const trackHomeClickCategoryTag = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickCategoryTag(additionalParams);
};

export const trackHomeClickGameEntryIcon = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickGameEntryIcon(additionalParams);
};

export const trackClickNavHome = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackClickNavHome(additionalParams);
};

export const trackClickNavPromotion = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackClickNavPromotion(additionalParams);
};

export const trackClickNavDeposit = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackClickNavDeposit(additionalParams);
};

export const trackClickNavWithdraw = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackClickNavWithdraw(additionalParams);
};

export const trackClickNavProfile = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackClickNavProfile(additionalParams);
};

export const trackHomeClickFloatingWindow = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackHomeClickFloatingWindow(additionalParams);
};

export const trackPromotionClickNavEvents = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavEvents(additionalParams);
};

export const trackPromotionClickNavRebate = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavRebate(additionalParams);
};

export const trackPromotionClickNavVip = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavVip(additionalParams);
};

export const trackPromotionClickNavRedemptionCode = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavRedemptionCode(additionalParams);
};

export const trackPromotionClickNavHistory = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavHistory(additionalParams);
};

export const trackPromotionClickNavPending = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickNavPending(additionalParams);
};

export const trackPromotionClickTreasureInvite = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickTreasureInvite(additionalParams);
};

export const trackPromotionClickWheel = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickWheel(additionalParams);
};

export const trackPromotionClickVip = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickVip(additionalParams);
};

export const trackPromotionClickAffiliate = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickAffiliate(additionalParams);
};

export const trackPromotionClickRebate = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickRebate(additionalParams);
};

export const trackPromotionClickRedEnvelope = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickRedEnvelope(additionalParams);
};

export const trackPromotionClickDailyRescue = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickDailyRescue(additionalParams);
};

export const trackPromotionClickWeeklyRescue = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPromotionClickWeeklyRescue(additionalParams);
};

export const trackPayClickNowButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackPayClickNowButton(additionalParams);
};

export const trackWithdrawClickMethodButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackWithdrawClickMethodButton(additionalParams);
};

export const trackWithdrawClickNowButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackWithdrawClickNowButton(additionalParams);
};

export const trackProfileClickDepositButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickDepositButton(additionalParams);
};

export const trackProfileClickWithdrawButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickWithdrawButton(additionalParams);
};

export const trackProfileClickVipButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickVipButton(additionalParams);
};

export const trackProfileClickReportsButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickReportsButton(additionalParams);
};

export const trackProfileClickInviteButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickInviteButton(additionalParams);
};

export const trackProfileClickRedemptionCodeButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickRedemptionCodeButton(additionalParams);
};

export const trackProfileClickSupportButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickSupportButton(additionalParams);
};

export const trackProfileClickSecurityCenterButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickSecurityCenterButton(additionalParams);
};

export const trackProfileClickLogoutButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickLogoutButton(additionalParams);
};

export const trackProfileClickInboxButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackProfileClickInboxButton(additionalParams);
};

export const trackMessageCenterClickNotificationButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickNotificationButton(additionalParams);
};

export const trackMessageCenterClickAnnouncementButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickAnnouncementButton(additionalParams);
};

export const trackMessageCenterClickOnlineSupportButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickOnlineSupportButton(additionalParams);
};

export const trackMessageCenterClickFacebookButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickFacebookButton(additionalParams);
};

export const trackMessageCenterClickTelegramButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickTelegramButton(additionalParams);
};

export const trackMessageCenterClickWhatsappButton = (additionalParams?: Record<string, any>) => {
  firebaseAnalyticsService.trackMessageCenterClickWhatsappButton(additionalParams);
};


export const trackError = (errorType: string, errorMessage: string, errorCode?: string) => {
  firebaseAnalyticsService.trackError(errorType, errorMessage, errorCode);
};

export const isAnalyticsInitialized = () => {
  return firebaseAnalyticsService.isAnalyticsInitialized();
};

/**
 * 初始化Firebase Analytics项目（使用本地配置）
 * 在App.vue中调用此函数来初始化Analytics专用的Firebase项目
 */
export const initializeAnalyticsApp = () => {
  return firebaseAnalyticsService.initializeAnalyticsApp();
};

// 导出服务实例（用于高级用法）
export default firebaseAnalyticsService;
